import { useCallback } from "react";
import { RELEASE_NOTES } from "@/pages/ReleaseNotes/data";
import { usePreference } from "@/contexts/PreferencesContext";

/**
 * お知らせの既読管理。
 *
 * PRODUCT_DEV-762 では既読を localStorage にしか持っていなかったため、書き込んでも
 * 他のコンポーネント（メニュー行・タブバー・ニュース画面）が再レンダーされず、
 * モジュールレベルの購読を自前で実装していた。
 *
 * PRODUCT_DEV-699 で既読を個人設定（サーバ）へ移したことで、値は Context が持つ状態に
 * なった。**購読は Context の再レンダーがそのまま担うので自前の仕組みは要らない。**
 * 端末をまたいで既読が引き継がれる副次効果もある（PC で読んだお知らせがスマホでも既読）。
 */
function getLatestVersion(): string {
  // Minizo ビルドは vite.config.ts の alias で RELEASE_NOTES が空配列に差し替わる
  // （PRODUCT_DEV-748）。したがって Minizo では常に "" ＝未読なしになる。
  return RELEASE_NOTES.length > 0 ? RELEASE_NOTES[0].version : "";
}

export function useReleaseNotes() {
  const [lastSeenVersion, setLastSeenVersion] = usePreference(
    "lastSeenReleaseVersion",
    "",
  );

  const latest = getLatestVersion();

  const markAsRead = useCallback(() => {
    if (!latest) return;
    if (lastSeenVersion === latest) return;
    setLastSeenVersion(latest);
  }, [latest, lastSeenVersion, setLastSeenVersion]);

  return {
    hasUnread: latest !== "" && lastSeenVersion !== latest,
    markAsRead,
    lastSeenVersion: lastSeenVersion || null,
  };
}
